import { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { motion } from 'framer-motion';

import { useAuth } from '../../auth/auth-context';
import { MOTION_DURATION, MOTION_EASE } from '../../lib/motion';
import { ROLE_LABEL } from '../../lib/roles';
import { AppLogo } from './app-logo';
import { SidebarNavigation } from './sidebar-navigation';
import { SignOutButton } from './sign-out-button';
import { UserAvatar } from './user-summary';

/**
 * Slide-over navigation for viewports below `lg`, listing the same entries as the
 * desktop sidebar.
 *
 * Implemented as a labelled `<dialog>` opened with `showModal()` so the browser
 * provides the Escape handler, the inert background and top-layer stacking.
 * `docs/ui-guidelines.md` section 8 requires Escape-to-close and focus restoration.
 *
 * Open state is derived rather than synchronised: the panel records the path it was
 * opened on and counts as open only while that is still the current path. Any
 * navigation — a link tap, the browser back button — therefore closes it without an
 * effect that resets state after the fact.
 */
export function MobileNavigation() {
  const location = useLocation();
  const { user } = useAuth();
  const [openedOn, setOpenedOn] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  const isOpen = openedOn === location.pathname;

  // Imperative bridge to the native dialog API; `open` has no declarative equivalent
  // that also traps the background.
  useEffect(() => {
    const dialog = dialogRef.current;

    if (dialog === null) {
      return;
    }

    if (isOpen && !dialog.open) {
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  const close = (): void => {
    setOpenedOn(null);
    triggerRef.current?.focus();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setOpenedOn(location.pathname);
        }}
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-haspopup="dialog"
        className="flex size-10 items-center justify-center rounded-md border border-line-subtle text-ink-secondary transition-colors duration-150 hover:border-line-strong hover:text-ink-primary lg:hidden"
      >
        <span className="sr-only">Open menu</span>
        <svg
          viewBox="0 0 20 20"
          className="size-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path d="M3 5.5h14M3 10h14M3 14.5h14" strokeLinecap="round" />
        </svg>
      </button>

      <dialog
        ref={dialogRef}
        id={panelId}
        aria-label="Menu"
        onClose={() => {
          if (isOpen) {
            close();
          }
        }}
        onClick={(event) => {
          // A click on the backdrop lands on the dialog element itself.
          if (event.target === dialogRef.current) {
            close();
          }
        }}
        className="m-0 mr-auto h-full max-h-none w-[min(20rem,85vw)] border-r border-line-subtle bg-surface-raised p-0 text-ink-primary backdrop:bg-scrim/80"
      >
        {isOpen ? (
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: MOTION_DURATION, ease: MOTION_EASE }}
            className="flex h-full flex-col"
          >
            <div className="flex h-16 shrink-0 items-center justify-between gap-3 border-b border-line-subtle px-4">
              <AppLogo />
              <button
                type="button"
                onClick={close}
                className="flex size-10 items-center justify-center rounded-md text-ink-secondary transition-colors duration-150 hover:bg-surface-hover hover:text-ink-primary"
              >
                <span className="sr-only">Close menu</span>
                <svg
                  viewBox="0 0 20 20"
                  className="size-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <path d="m5.5 5.5 9 9M14.5 5.5l-9 9" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <nav aria-label="Primary" className="flex-1 overflow-y-auto p-3">
              {/* Covers tapping the link for the current page, where the path does not change. */}
              <SidebarNavigation
                onNavigate={() => {
                  setOpenedOn(null);
                }}
              />
            </nav>

            <div className="flex shrink-0 flex-col gap-3 border-t border-line-subtle px-4 py-4">
              {user === null ? null : (
                <div className="flex min-w-0 items-center gap-3">
                  <UserAvatar user={user} />
                  <p className="flex min-w-0 flex-col">
                    <span className="truncate text-sm text-ink-primary">{user.fullName}</span>
                    <span className="truncate text-xs text-ink-muted">
                      {ROLE_LABEL[user.role]} · {user.email}
                    </span>
                  </p>
                </div>
              )}
              <SignOutButton variant="secondary" size="md" className="w-full" />
              <p className="text-xs text-ink-muted">
                Authorized access only. Every project open is recorded.
              </p>
            </div>
          </motion.div>
        ) : null}
      </dialog>
    </>
  );
}
